/**
 * RunnerProfile - Runner profile display
 * Follows ReporterProfile.tsx pattern
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useSubscriptionStore } from '@/stores/useSubscriptionStore';
import { cn } from '@/utils/cn';
import { ActivityCard } from './ActivityCard';

interface RunnerProfileProps {
  userId?: string;
  onUserClick?: (userId: string) => void;
}

export function RunnerProfile({ userId, onUserClick }: RunnerProfileProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { tier, subscription } = useSubscriptionStore();
  const viewingRunner = useRunningStore((state) => state.viewingRunner);
  const runnerActivities = useRunningStore((state) => state.runnerActivities);
  const fetchRunnerProfile = useRunningStore((state) => state.fetchRunnerProfile);
  const fetchRunnerActivities = useRunningStore((state) => state.fetchRunnerActivities);
  const followRunner = useRunningStore((state) => state.followRunner);
  const unfollowRunner = useRunningStore((state) => state.unfollowRunner);

  const [activeTab, setActiveTab] = useState<'activities' | 'stats' | 'badges'>('activities');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      // Load specific user profile or current user's profile
      const profileId = userId || (isAuthenticated && user?.id);
      if (profileId) {
        await fetchRunnerProfile(profileId);
        await fetchRunnerActivities(profileId);
      }
      setIsLoading(false);
    };
    loadProfile();
  }, [userId, isAuthenticated, user?.id, fetchRunnerProfile, fetchRunnerActivities]);

  const handleFollow = async () => {
    if (!viewingRunner) return;
    if (viewingRunner.is_following) {
      await unfollowRunner(viewingRunner.id);
    } else {
      await followRunner(viewingRunner.id);
    }
  };

  const formatPace = (seconds: number | null): string => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Blurp.ie branded runner icon - sprinting figure with motion lines
  const BlurpieRunnerIcon = ({ size = 48 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      {/* Motion lines */}
      <rect x="0" y="20" width="15" height="4" rx="2" fill="#06b6d4" />
      <rect x="3" y="28" width="13" height="4" rx="2" fill="#06b6d4" />
      <rect x="0" y="36" width="15" height="4" rx="2" fill="#06b6d4" />
      {/* Head - positioned at top of torso */}
      <circle cx="46" cy="16" r="6" fill="#06b6d4" />
      {/* Torso - leaning forward */}
      <path d="M44 22L38 38" stroke="#06b6d4" strokeWidth="6" strokeLinecap="round" />
      {/* Back arm - bent elbow pointing back */}
      <path d="M42 26L50 30L56 24" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {/* Front arm - bent elbow pointing forward */}
      <path d="M40 28L32 24L24 28" stroke="#06b6d4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {/* Back leg - extended straight back (to the left) */}
      <path d="M38 38L24 48L14 56" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Front leg - thigh forward, lower leg curling inward */}
      <path d="M38 38L52 48L46 60" stroke="#06b6d4" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  if (!viewingRunner && !userId && !isAuthenticated) {
    // Show sign-in prompt only if not authenticated
    return (
      <div className="p-4">
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-slate-800/80 border border-emerald-500/30 flex items-center justify-center">
            <BlurpieRunnerIcon size={48} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Your Profile</h2>
          <p className="text-text-secondary mb-6">
            Sign in to track your running stats and connect with other runners
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-xl bg-emerald-500 text-white font-medium"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (!viewingRunner && isAuthenticated) {
    // User is authenticated but profile data is loading or not found
    return (
      <div className="p-4">
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-slate-800/80 border border-emerald-500/30 flex items-center justify-center">
            <BlurpieRunnerIcon size={48} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Welcome, Runner!</h2>
          <p className="text-text-secondary mb-4">
            {user?.email || 'Start tracking your runs with air quality insights'}
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
            <div className="p-3 bg-slate-800/50 rounded-xl text-center">
              <div className="text-2xl font-bold text-white">0</div>
              <div className="text-xs text-text-secondary">Runs</div>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-xl text-center">
              <div className="text-2xl font-bold text-white">0</div>
              <div className="text-xs text-text-secondary">km</div>
            </div>
          </div>
          <p className="text-text-secondary text-sm mt-6">
            Record your first run to see your stats here!
          </p>
        </div>
      </div>
    );
  }

  if (!viewingRunner) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800/80 border border-slate-600 flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-secondary">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
        <p className="text-text-secondary">Runner not found</p>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Profile Header */}
      <div className="p-4 space-y-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="relative">
            {viewingRunner.avatar_url ? (
              <img
                src={viewingRunner.avatar_url}
                alt={viewingRunner.username}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <span className="text-3xl">🏃</span>
              </div>
            )}
            {viewingRunner.is_pro && (
              <span className="absolute -bottom-1 -right-1 text-lg">⭐</span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">
              {viewingRunner.first_name || viewingRunner.username}
              {viewingRunner.last_name && ` ${viewingRunner.last_name}`}
            </h2>
            <p className="text-text-secondary text-sm">@{viewingRunner.username}</p>
            {viewingRunner.bio && (
              <p className="text-text-secondary text-sm mt-2">{viewingRunner.bio}</p>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 p-3 bg-slate-800/50 rounded-xl">
          <div className="text-center">
            <div className="text-lg font-bold text-white">{viewingRunner.total_runs}</div>
            <div className="text-[10px] text-text-secondary">Runs</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-white">{viewingRunner.total_distance_km.toFixed(0)}</div>
            <div className="text-[10px] text-text-secondary">km</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-white">{viewingRunner.followers_count}</div>
            <div className="text-[10px] text-text-secondary">Followers</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-white">{viewingRunner.following_count}</div>
            <div className="text-[10px] text-text-secondary">Following</div>
          </div>
        </div>

        {/* AQI Stats - Blurp.ie unique */}
        <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <span>☁️</span>
            <span className="text-xs text-cyan-400 font-medium">Air Quality Stats</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-white">
                {viewingRunner.average_aqi_exposure || '--'}
              </div>
              <div className="text-[10px] text-text-secondary">Avg AQI</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">
                {viewingRunner.cleanest_run_aqi || '--'}
              </div>
              <div className="text-[10px] text-text-secondary">Best AQI</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white">
                {viewingRunner.total_scent_reports_while_running}
              </div>
              <div className="text-[10px] text-text-secondary">Scents</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {userId && (
          <div className="flex gap-2">
            <button
              onClick={handleFollow}
              className={cn(
                'flex-1 py-3 rounded-xl font-medium transition-all',
                viewingRunner.is_following
                  ? 'bg-slate-800 text-white border border-glass-border'
                  : 'bg-emerald-500 text-white'
              )}
            >
              {viewingRunner.is_following ? 'Following' : 'Follow'}
            </button>
            <button className="px-6 py-3 rounded-xl bg-slate-800 text-white border border-glass-border">
              💬
            </button>
          </div>
        )}
      </div>

      {/* Subscription — own profile only */}
      {!userId && (
        <div className="px-4 mb-4">
          <div className="p-4 bg-slate-800/50 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold',
                tier === 'pro' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-700 text-slate-400'
              )}>
                {tier === 'pro' ? '⭐' : '○'}
              </div>
              <div>
                <p className="text-sm font-semibold text-white capitalize">{tier === 'pro' ? 'Pro' : 'Free'} Plan</p>
                <p className="text-[11px] text-text-secondary">
                  {tier === 'pro' && subscription?.current_period_end
                    ? subscription.cancel_at_period_end
                      ? `Cancels ${new Date(subscription.current_period_end).toLocaleDateString()}`
                      : `Renews ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    : tier === 'pro'
                    ? 'Active'
                    : 'Upgrade for route planning'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/billing')}
              className="text-[12px] font-medium text-cyan-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20"
            >
              {tier === 'pro' ? 'Manage' : 'Upgrade'}
            </button>
          </div>
        </div>
      )}

      {/* Personal Records */}
      <div className="px-4 mb-4">
        <h3 className="text-sm font-medium text-white mb-2">Personal Records</h3>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 bg-slate-800/50 rounded-xl">
            <div className="text-xs text-text-secondary mb-1">5K</div>
            <div className="text-lg font-bold text-white">
              {formatPace(viewingRunner.best_5k_seconds)}
            </div>
          </div>
          <div className="p-3 bg-slate-800/50 rounded-xl">
            <div className="text-xs text-text-secondary mb-1">10K</div>
            <div className="text-lg font-bold text-white">
              {formatPace(viewingRunner.best_10k_seconds)}
            </div>
          </div>
          <div className="p-3 bg-slate-800/50 rounded-xl">
            <div className="text-xs text-text-secondary mb-1">Half Marathon</div>
            <div className="text-lg font-bold text-white">
              {formatPace(viewingRunner.best_half_marathon_seconds)}
            </div>
          </div>
          <div className="p-3 bg-slate-800/50 rounded-xl">
            <div className="text-xs text-text-secondary mb-1">Marathon</div>
            <div className="text-lg font-bold text-white">
              {formatPace(viewingRunner.best_marathon_seconds)}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 flex gap-2 mb-4">
        {[
          { id: 'activities' as const, label: 'Activities' },
          { id: 'stats' as const, label: 'Stats' },
          { id: 'badges' as const, label: 'Badges' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-text-secondary hover:text-white'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'activities' && (
        <div className="px-4 space-y-4">
          {runnerActivities.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl mb-2 block">🏃</span>
              <p className="text-text-secondary">No activities yet</p>
            </div>
          ) : (
            runnerActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onUserClick={onUserClick}
              />
            ))
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="px-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <div className="text-2xl font-bold text-white">
                {viewingRunner.total_distance_km.toFixed(1)}
              </div>
              <div className="text-xs text-text-secondary">Total km</div>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <div className="text-2xl font-bold text-white">
                {formatDuration(viewingRunner.total_duration_hours)}
              </div>
              <div className="text-xs text-text-secondary">Total time</div>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <div className="text-2xl font-bold text-white">
                {viewingRunner.total_elevation_meters.toLocaleString()}
              </div>
              <div className="text-xs text-text-secondary">Elevation (m)</div>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <div className="text-2xl font-bold text-orange-400">
                {viewingRunner.kudos_received}
              </div>
              <div className="text-xs text-text-secondary">Kudos received</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'badges' && (
        <div className="px-4">
          {viewingRunner.badges.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl mb-2 block">🏆</span>
              <p className="text-text-secondary">No badges earned yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {viewingRunner.badges.map((badge) => (
                <div
                  key={badge.id}
                  className="p-3 bg-slate-800/50 rounded-xl text-center"
                >
                  <div className="text-2xl mb-1">{badge.icon}</div>
                  <div className="text-xs text-white font-medium">{badge.name}</div>
                  <div className={cn(
                    'text-[10px] capitalize',
                    badge.tier === 'gold' && 'text-yellow-400',
                    badge.tier === 'silver' && 'text-gray-300',
                    badge.tier === 'bronze' && 'text-orange-400',
                    badge.tier === 'platinum' && 'text-cyan-400'
                  )}>
                    {badge.tier}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RunnerProfile;
